import React, { useState,useEffect, useCallback } from 'react';
import { useLocation, useNavigate} from 'react-router-dom';
import { useWellnessSummaryData } from '../hooks/wellnessSummaryData';
import { useRecommendationsTextData } from '../hooks/recommendationsText';
import { useEducationTextData } from '../hooks/educationText';
import {
  VictoryAxis,
  VictoryChart,
  VictoryErrorBar,
  VictoryScatter,
  VictoryLine,
} from 'victory';
import '../styles/VisualizeWellnessData.css';

const VisualizeWellnessData = () => {
    const location = useLocation();
    const navigate = useNavigate();
    
    // Health data visualization state
    const [selectedTimeSpan, setSelectedTimeSpan] = useState('week');
    const [currentDate, setCurrentDate] = useState(new Date());
    const [healthData, setHealthData] = useState([]);
    const [dataType, setDataType] = useState('heartrate');
    const [patientId, setPatientId] = useState(null);
    
    // Get patient ID from localStorage and handle location state
    useEffect(() => {
        if (localStorage.getItem('user_info') !== null) {
            const userInfo = JSON.parse(localStorage.getItem('user_info'));
            setPatientId(userInfo.di_id);
        }
        
        // Set data type from location state if provided
        if (location.state && location.state.dataType) {
            setDataType(location.state.dataType);
        }
    }, [location.state]);
    
    // Call the wellness summary API
    const { data: wellnessData, isLoading, error } = useWellnessSummaryData(patientId);
    
    // Call the recommendations text API
    const { data: recommendationsData, isLoading: recommendationsLoading, error: recommendationsError } = useRecommendationsTextData(patientId);
    
    // Function to map dataType to topic for Education API
    const getTopicFromDataType = () => {
        switch(dataType) {
            case 'heartrate':
                return 'heartrate';
            case 'hrv':
                return 'hrv';
            case 'systolic':
                return 'bloodpressure';
            case 'diastolic':
                return 'bloodpressure';
            case 'stress':
                return 'hrv';
            case 'dailysteps':
                return 'activity';
            case 'dailycalories':
                return 'activity';
            case 'dailydistance':
                return 'activity';
            case 'spo2':
                return 'spo2';
            case 'vo2max':
                return 'vo2max';
            default:
                return null;
        }
    };
    
    // Get topic based on selected data type
    const topic = getTopicFromDataType();
    
    // Call the education text API
    const { data: educationData, isLoading: educationLoading, error: educationError } = useEducationTextData(topic);
    
    // State for education modal
    const [showEducation, setShowEducation] = useState(false);
    const [educationHtml, setEducationHtml] = useState(null);
    
    // Handle data type changes
    const handleDataTypeChange = (newDataType) => {
        setDataType(newDataType);
    };
    
    // Function to get trend text for current data type
    const getTrendText = () => {
        // Check if recommendations data exists and has no errors
        if (!recommendationsData || recommendationsData.errors) {
            return null;
        }
        
        const trendData = recommendationsData;
        const date = recommendationsData.summary_date ? recommendationsData.summary_date : new Date().toISOString();
        
        // Format date to show only day, month, year
        // Parse date string to avoid timezone conversion issues
        let dateObj;
        if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}/)) {
            // Date string in YYYY-MM-DD or YYYY-MM-DD HH:MM:SS format - parse it in local timezone
            // Extract just the date part (before space or 'T')
            const datePart = date.split(/[\sT]/)[0];
            const [year, month, day] = datePart.split('-').map(Number);
            dateObj = new Date(year, month - 1, day); // month is 0-indexed
        } else {
            // Use Date constructor for other formats
            dateObj = new Date(date);
        }
        
        const formattedDate = dateObj.toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
        
        // Map dataType to the API keys
        const vitalTypeMapping = {
            'heartrate': 'heartrate',
            'hrv': 'hrv',
            'systolic': 'systolic',
            'diastolic': 'diastolic',
            'stress': 'stress',
            'dailysteps': 'dailysteps',
            'dailycalories': 'dailycalories',
            'dailydistance': 'dailydistance'
        };
        
        const vitalType = vitalTypeMapping[dataType];
        if (!vitalType) {
            return null;
        }
        
        // Get the trend data for the specific vital type
        const minDirection = trendData[`${vitalType}_min_val_direction`];
        const minTrend = trendData[`${vitalType}_min_val_trend`];
        const maxDirection = trendData[`${vitalType}_max_val_direction`];
        const maxTrend = trendData[`${vitalType}_max_val_trend`];
        const avgDirection = trendData[`${vitalType}_avg_val_direction`];
        const avgTrend = trendData[`${vitalType}_avg_val_trend`];
        
        if (!minDirection || minTrend === undefined) {
            return null;
        }
        
        // Format the trend text with all three values
        const trendText = `Trend on date ${formattedDate}\nFor ${dataType}:\nMin: ${minDirection} (${minTrend})\nMax: ${maxDirection} (${maxTrend})\nAvg: ${avgDirection} (${avgTrend})`;
        
        return trendText;
    };
    
    // Function to get information text for current vital type
    const getVitalInformationText = () => {
        switch(dataType) {
            case 'heartrate':
                return "RHR Normal range: 50-90, lower is better. Actions to improve: Walk/Run regularly";
            case 'hrv':
                return "HRV Normal range: 20-100, higher is better. Actions to improve: Sleep on time. Do meditation."; // Add HRV information text here
            case 'systolic':
                return "Systolic BP Normal range: 105-125, lower is better. Actions to improve: Eat healthy food. Do exercise."; // Add Systolic BP information text here
            case 'diastolic':
                return "Diastolic BP Normal range: 70-90, lower is better. Actions to improve: Eat healthy food. Do exercise."; // Add Diastolic BP information text here
            case 'stress':
                return "Stress Level Normal range: 0-25, lower is better. Actions to improve: Sleep on time. Do meditation."; // Add Stress Level information text here
            case 'dailysteps':
                return "Daily Steps Normal range: 5000-10000, higher is better. Actions to improve: Walk/Run regularly."; // Add Daily Steps information text here
            case 'dailycalories':
                return "Daily Calories Normal range: 100-1000, higher is better. Actions to improve: Eat healthy food. Do exercise."; // Add Daily Calories information text here
            case 'dailydistance':
                return "Daily Distance Normal range: 100-1000, higher is better. Actions to improve: Walk/Run regularly."; // Add Daily Distance information text here
            case 'spo2':
                return "Blood Oxygen Normal range: 90-100, higher is better. Actions to improve: Eat healthy food. Do exercise."; // Add Blood Oxygen information text here
            case 'vo2max':
                return "VO2 Max Normal range: 0-100, higher is better. Actions to improve: Eat healthy food. Do exercise."; // Add VO2 Max information text here
            default:
                return "";
        }
    };
    
    // Function to fetch HTML content from presigned URL
    const fetchHtmlFromUrl = async (url) => {
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const html = await response.text();
            return html;
        } catch (error) {
            console.error('Error fetching HTML from presigned URL:', error);
            throw error;
        }
    };
    
    // Effect to handle education data (HTML string or presigned URL)
    useEffect(() => {
        if (educationData) {
            // Check if it's a presigned URL (starts with http/https)
            if (typeof educationData === 'string' && (educationData.startsWith('http://') || educationData.startsWith('https://'))) {
                fetchHtmlFromUrl(educationData)
                    .then(html => setEducationHtml(html))
                    .catch(error => {
                        console.error('Failed to fetch HTML from presigned URL:', error);
                        setEducationHtml(null);
                    });
            } else {
                // It's already HTML content
                setEducationHtml(educationData);
            }
        } else {
            setEducationHtml(null);
        }
    }, [educationData]);
    
    // Auto-set current date to match available data when switching time spans or data types
    useEffect(() => {
        if (wellnessData && wellnessData[dataType] && wellnessData[dataType][selectedTimeSpan]) {
            const timeSpanData = wellnessData[dataType][selectedTimeSpan];
            if (timeSpanData.length > 0) {
                // Get the first available date from the data
                const firstDataKey = Object.keys(timeSpanData[0])[0];
                let availableDate;
                
                if (selectedTimeSpan === 'day') {
                    // For daily data, extract date part from "YYYY-MM-DD HH"
                    const datePart = firstDataKey.split(' ')[0];
                    const [year, month, day] = datePart.split('-').map(Number);
                    availableDate = new Date(year, month - 1, day); // month is 0-indexed
                } else {
                    // For weekly/monthly data, use the full key
                    const [year, month, day] = firstDataKey.split('-').map(Number);
                    availableDate = new Date(year, month - 1, day); // month is 0-indexed
                }
                
                // Only auto-set if dates are different
                if (availableDate.toDateString() !== currentDate.toDateString()) {
                    setCurrentDate(availableDate);
                }
            }
        }
    }, [selectedTimeSpan, wellnessData, dataType]);
    
    // Get health data from API response
    const getHealthData = useCallback((timeSpan, date) => {
        const data = [];
        
        // If we have API data, use the structured format
        if (wellnessData && wellnessData[dataType]) {
            const vitalData = wellnessData[dataType];
            const timeSpanData = vitalData[timeSpan] || [];
            

            
            // Calculate the date range based on current date and time span
            let startDate, endDate;
            let expectedDataPoints = [];
            
            switch(timeSpan) {
                case 'day':
                    startDate = new Date(date);
                    startDate.setHours(0, 0, 0, 0);
                    endDate = new Date(date);
                    endDate.setHours(23, 59, 59, 999);
                    // Create 24 hourly data points
                    for (let i = 0; i < 24; i++) {
                        const hourDate = new Date(startDate);
                        hourDate.setHours(i);
                        expectedDataPoints.push(hourDate);
                    }
                    break;
                case 'week':
                    startDate = new Date(date);
                    startDate.setDate(date.getDate() - date.getDay()); // Start of week (Sunday)
                    startDate.setHours(0, 0, 0, 0);
                    endDate = new Date(startDate);
                    endDate.setDate(startDate.getDate() + 6); // End of week (Saturday)
                    endDate.setHours(23, 59, 59, 999);
                    // Create 7 daily data points
                    for (let i = 0; i < 7; i++) {
                        const dayDate = new Date(startDate);
                        dayDate.setDate(startDate.getDate() + i);
                        expectedDataPoints.push(dayDate);
                    }
                    break;
                case 'month':
                    startDate = new Date(date.getFullYear(), date.getMonth(), 1);
                    endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
                    endDate.setHours(23, 59, 59, 999);
                    // Create daily data points for the entire month
                    const daysInMonth = endDate.getDate();
                    for (let i = 0; i < daysInMonth; i++) {
                        const dayDate = new Date(startDate);
                        dayDate.setDate(startDate.getDate() + i);
                        expectedDataPoints.push(dayDate);
                    }
                    break;
                case 'year':
                    startDate = new Date(date.getFullYear(), 0, 1);
                    endDate = new Date(date.getFullYear(), 11, 31);
                    endDate.setHours(23, 59, 59, 999);
                    // Create 12 monthly data points
                    for (let i = 0; i < 12; i++) {
                        try {
                            const monthDate = new Date(date.getFullYear(), i, 1);
                            // Validate the date before adding it
                            if (!isNaN(monthDate.getTime())) {
                                expectedDataPoints.push(monthDate);
                            } else {
                                console.error('Invalid month date created:', date.getFullYear(), i);
                            }
                        } catch (error) {
                            console.error('Error creating month date:', error, 'year:', date.getFullYear(), 'month:', i);
                        }
                    }
                    break;
                default:
                    return [];
            }
            

            
            // Filter data based on date range
            const filteredData = timeSpanData.filter(item => {
                const dateKey = Object.keys(item)[0];
                let itemDate;
                
                if (timeSpan === 'day') {
                    // For daily data, the key format is "YYYY-MM-DD HH"
                    // Extract just the date part for comparison
                    const datePart = dateKey.split(' ')[0];
                    // Parse date explicitly to avoid timezone issues
                    const [year, month, day] = datePart.split('-').map(Number);
                    itemDate = new Date(year, month - 1, day); // month is 0-indexed
                } else {
                    // For weekly/monthly/yearly data, the key format is "YYYY-MM-DD"
                    try {
                        const [year, month, day] = dateKey.split('-').map(Number);
                        itemDate = new Date(year, month - 1, day); // month is 0-indexed
                        // Validate the date
                        if (isNaN(itemDate.getTime())) {
                            console.error('Invalid date parsed:', dateKey, 'year:', year, 'month:', month, 'day:', day);
                            return false; // Skip this item
                        }
                    } catch (error) {
                        console.error('Error parsing date key:', error, 'dateKey:', dateKey);
                        return false; // Skip this item
                    }
                }
                
                return itemDate >= startDate && itemDate <= endDate;
            });
            

            
            // Create a map of available data by date string
            const dataMap = new Map();
            filteredData.forEach(item => {
                const dateKey = Object.keys(item)[0];
                const dataObj = item[dateKey];
                
                if (timeSpan === 'day') {
                    // For daily data, use the full key (date + hour) as the map key
                    dataMap.set(dateKey, dataObj);
                } else {
                    // For weekly/monthly data, use just the date
                    dataMap.set(dateKey, dataObj);
                }
            });
            
            // Create complete timeline with all expected data points
            expectedDataPoints.forEach((expectedDate, index) => {
                let dateKey;
                let availableData;
                
                if (timeSpan === 'day') {
                    // For daily data, create key in format "YYYY-MM-DD HH" (zero-padded hour)
                    // Use local date components instead of UTC to avoid timezone issues
                    const year = expectedDate.getFullYear();
                    const month = (expectedDate.getMonth() + 1).toString().padStart(2, '0');
                    const day = expectedDate.getDate().toString().padStart(2, '0');
                    const dateStr = `${year}-${month}-${day}`;
                    const hour = expectedDate.getHours().toString().padStart(2, '0'); // Zero-pad to match API format
                    dateKey = `${dateStr} ${hour}`;
                    availableData = dataMap.get(dateKey);
                    

                } else {
                    // For weekly/monthly/yearly data, use YYYY-MM-DD format
                    try {
                        dateKey = expectedDate.toISOString().split('T')[0];
                        availableData = dataMap.get(dateKey);
                    } catch (error) {
                        console.error('Invalid date error:', error, 'expectedDate:', expectedDate);
                        // Fallback: create a safe date key
                        const year = expectedDate.getFullYear();
                        const month = (expectedDate.getMonth() + 1).toString().padStart(2, '0');
                        const day = expectedDate.getDate().toString().padStart(2, '0');
                        dateKey = `${year}-${month}-${day}`;
                        availableData = dataMap.get(dateKey);
                    }
                }
                
                if (availableData) {
                    // Data exists for this date/time
                    data.push({
                        x: index + 1,
                        y: availableData.avg,
                        min: availableData.min,
                        max: availableData.max,
                        hasData: true
                    });
                } else {
                    // No data for this date/time - create empty data point
                    data.push({
                        x: index + 1,
                        y: null, // null will be handled by Victory chart
                        min: null,
                        max: null,
                        hasData: false
                    });
                }
            });
            

            return data;
        }
        

        // Fallback: return empty data if no API data
        return [];
    }, [wellnessData, dataType]);





    // Navigation functions
    const goBackward = () => {
        const newDate = new Date(currentDate);
        switch(selectedTimeSpan) {
            case 'day':
                newDate.setDate(newDate.getDate() - 1);
                break;
            case 'week':
                newDate.setDate(newDate.getDate() - 7);
                break;
            case 'month':
                newDate.setMonth(newDate.getMonth() - 1);
                break;
            case 'year':
                newDate.setFullYear(newDate.getFullYear() - 1);
                break;
            default:
                break;
        }
        setCurrentDate(newDate);
    };

    const goForward = () => {
        const newDate = new Date(currentDate);
        switch(selectedTimeSpan) {
            case 'day':
                newDate.setDate(newDate.getDate() + 1);
                break;
            case 'week':
                newDate.setDate(newDate.getDate() + 7);
                break;
            case 'month':
                newDate.setMonth(newDate.getMonth() + 1);
                break;
            case 'year':
                newDate.setFullYear(newDate.getFullYear() + 1);
                break;
            default:
                break;
        }
        setCurrentDate(newDate);
    };

    // Update health data when time span, date, or data type changes
    useEffect(() => {
        console.log('Updating health data for:', selectedTimeSpan, 'date:', currentDate);
        const data = getHealthData(selectedTimeSpan, currentDate);
        setHealthData(data);
    }, [selectedTimeSpan, currentDate, getHealthData]);



    // Format date for display
    const formatDisplayDate = () => {
        switch(selectedTimeSpan) {
            case 'day':
                return currentDate.toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                });
            case 'week':
                const weekStart = new Date(currentDate);
                weekStart.setDate(currentDate.getDate() - currentDate.getDay());
                const weekEnd = new Date(weekStart);
                weekEnd.setDate(weekStart.getDate() + 6);
                return `${weekStart.toLocaleDateString()} - ${weekEnd.toLocaleDateString()}`;
            case 'month':
                return currentDate.toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long' 
                });
            case 'year':
                return currentDate.getFullYear().toString();
            default:
                return '';
        }
    };

    return (
        <div className="min-h-screen pb-32 safe-area-top">
            {/* Black background */}
            <div className="absolute inset-0 bg-black"></div>
            
            <div className="container mx-auto py-6 relative z-10" style={{ paddingLeft: '5px', paddingRight: '5px', paddingBottom: '80px' }}>
        
        {/* Back Button */}
        <div className="mb-4">
            <button
                onClick={() => navigate(-1)}
                className="flex items-center text-white hover:text-blue-400 transition-colors duration-200"
            >
                <svg
                    className="w-5 h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                    />
                </svg>
                <span className="text-sm font-medium text-white">Go Back</span>
            </button>
        </div>
        
        {/* Loading State */}
        {isLoading && (
            <div className="mb-6">
                <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-white">Loading wellness data...</span>
                </div>
            </div>
        )}
        
        {/* Error State */}
        {error && (
            <div className="bg-[#161B22] border border-[#30363D] rounded-lg p-4 mb-6">
                <div className="flex items-center">
                    <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span className="text-red-400">Error loading wellness data: {error.message}</span>
                </div>
            </div>
        )}
        
        {/* Health Data Content - Card removed, content directly on page */}
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">
                    {dataType === 'heartrate' ? 'Heart Rate' : 
                     dataType === 'hrv' ? 'Heart Rate Variability' :
                     dataType === 'systolic' ? 'Systolic BP' :
                     dataType === 'diastolic' ? 'Diastolic BP' :
                     dataType === 'stress' ? 'Stress Level' :
                     dataType === 'dailysteps' ? 'Daily Steps' :
                     dataType === 'dailycalories' ? 'Daily Calories' :
                     dataType === 'dailydistance' ? 'Daily Distance' :
                     dataType === 'spo2' ? 'Blood Oxygen' :
                     dataType === 'vo2max' ? 'VO2 Max' : 'Health Data'}
                </h3>
                <div className="text-sm text-white">{formatDisplayDate()}</div>
            </div>
            
            {/* Data Type Selector */}
            {wellnessData && (
                <div className="mb-4">
                    <label className="block text-sm font-medium text-white mb-2">Select Vital Type:</label>
                    <select
                        value={dataType}
                        onChange={(e) => handleDataTypeChange(e.target.value)}
                        className="w-full px-3 py-2 border border-[#30363D] bg-[#161B22] text-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        style={{ 
                            colorScheme: 'dark',
                            backgroundColor: '#161B22',
                            color: '#F0F6FC',
                            WebkitAppearance: 'none',
                            MozAppearance: 'none',
                            appearance: 'none'
                        }}
                    >
                        {Object.keys(wellnessData)
                        .filter(vitalType => vitalType !== 'systolic' && vitalType !== 'diastolic')
                        .map((vitalType) => (
                            <option key={vitalType} value={vitalType} style={{ backgroundColor: '#161B22', color: '#F0F6FC' }}>
                                {vitalType === 'heartrate' ? 'Heart Rate' : 
                                 vitalType === 'hrv' ? 'Heart Rate Variability' :
                                 //vitalType === 'systolic' ? 'Systolic BP' :
                                 //vitalType === 'diastolic' ? 'Diastolic BP' :
                                 vitalType === 'stress' ? 'Stress Level' :
                                 vitalType === 'dailysteps' ? 'Daily Steps' :
                                 vitalType === 'dailycalories' ? 'Daily Calories' :
                                 vitalType === 'dailydistance' ? 'Daily Distance' : vitalType}
                            </option>
                        ))}
                    </select>
                </div>
            )}
            
            {/* Show message if selected data type is not available in API */}
            {wellnessData && !wellnessData[dataType] && (
                <div className="mb-4 p-3 bg-[#161B22] border border-[#30363D] rounded-md">
                    <div className="flex items-center">
                        <svg className="w-5 h-5 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <span className="text-yellow-400 text-sm">
                            {dataType} data is not available in the API response. Available types: {Object.keys(wellnessData).join(', ')}
                        </span>
                    </div>
                </div>
            )}
            
            {/* Time Span Tabs */}
            <div className="flex space-x-1 mb-4 bg-[#21262D] rounded-lg p-1">
                {['day', 'week', 'month', 'year'].map((span) => (
                    <button
                        key={span}
                        onClick={() => setSelectedTimeSpan(span)}
                        className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors ${
                            selectedTimeSpan === span
                                ? 'bg-[#58A6FF] text-white shadow-sm'
                                : 'text-white hover:text-blue-400'
                        }`}
                    >
                        {span.charAt(0).toUpperCase() + span.slice(1)}
                    </button>
                ))}
            </div>
            
            {/* Navigation Controls */}
            <div className="flex items-center justify-between mb-4">
                <button
                    onClick={goBackward}
                    className="p-2 text-white hover:text-blue-400 hover:bg-[#21262D] rounded-md transition-colors"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
                <div className="text-sm text-white font-medium">
                    {formatDisplayDate()}
                </div>
                <button
                    onClick={goForward}
                    className="p-2 text-white hover:text-blue-400 hover:bg-[#21262D] rounded-md transition-colors"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>
            
            {/* Chart Legend and Data Summary */}
            <div className="mb-2 text-xs text-white flex items-center justify-between">
                <div className="flex items-center space-x-4">
                    <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full mr-1" style={{
                            backgroundColor: dataType === 'heartrate' ? "#ef4444" :
                                           dataType === 'hrv' ? "#3b82f6" :
                                           dataType === 'systolic' ? "#dc2626" :
                                           dataType === 'diastolic' ? "#b91c1c" :
                                           dataType === 'stress' ? "#f59e0b" :
                                           dataType === 'dailysteps' ? "#3b82f6" :
                                           dataType === 'dailycalories' ? "#10b981" :
                                           dataType === 'dailydistance' ? "#8b5cf6" :
                                           dataType === 'spo2' ? "#10b981" :
                                           dataType === 'vo2max' ? "#8b5cf6" : "#ef4444"
                        }}></div>
                        <span>Data Available</span>
                    </div>
                    <div className="flex items-center">
                        <div className="w-2 h-2 transform rotate-45 mr-1" style={{
                            backgroundColor: "#d1d5db",
                            border: "1px solid #9ca3af"
                        }}></div>
                        <span>No Data</span>
                    </div>
                </div>
                {healthData.length > 0 && (
                    <div className="text-right">
                        <span className="font-medium">
                            {healthData.filter(d => d.hasData).length} of {healthData.length} data points available
                        </span>
                    </div>
                )}
            </div>
            
            {/* Health Data Chart */}
            <div className="h-64">
                {healthData.length > 0 ? (
                <VictoryChart
                    domain={{ 
                        y: dataType === 'heartrate' ? [40, 120] :
                            dataType === 'hrv' ? [0, 100] :
                            dataType === 'systolic' ? [90, 140] :
                            dataType === 'diastolic' ? [50, 90] :
                            dataType === 'stress' ? [0, 100] :
                            dataType === 'dailysteps' ? [0, 15000] :
                            dataType === 'dailycalories' ? [0, 500] :
                            dataType === 'dailydistance' ? [0, 10] :
                            dataType === 'spo2' ? [90, 100] :
                            dataType === 'vo2max' ? [20, 60] : [0, 100],
                        x: healthData.length > 0 ? [0, healthData.length + 1] :
                            selectedTimeSpan === 'day' ? [0, 25] : 
                            selectedTimeSpan === 'week' ? [0, 8] :
                            selectedTimeSpan === 'month' ? 
                            (() => {
                                const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
                                return [0, daysInMonth + 1];
                            })() : [0, 13]
                    }}
                    height={250}
                    padding={{ top: 20, bottom: 50, left: 60, right: 5 }}
                    style={{
                        background: { fill: 'transparent' }
                    }}
                >
                    <VictoryAxis
                        tickValues={healthData.length > 0 ? 
                            (() => {
                                // Show fewer tick marks for better readability
                                switch(selectedTimeSpan) {
                                    case 'day':
                                        // Show every 3 hours: 1, 4, 7, 10, 13, 16, 19, 22
                                        return [1, 4, 7, 10, 13, 16, 19, 22];
                                    case 'week':
                                        // Show all 7 days
                                        return [1, 2, 3, 4, 5, 6, 7];
                                    case 'month':
                                        // Show every 3rd day: 1, 4, 7, 10, 13, 16, 19, 22, 25, 28
                                        const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
                                        const monthTicks = [];
                                        for (let i = 1; i <= daysInMonth; i += 3) {
                                            monthTicks.push(i);
                                        }
                                        return monthTicks;
                                    case 'year':
                                        // Show all 12 months
                                        return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
                                    default:
                                        return healthData.map((_, index) => index + 1);
                                }
                            })() :
                            selectedTimeSpan === 'day' ? 
                            [1, 4, 7, 10, 13, 16, 19, 22] :
                            selectedTimeSpan === 'week' ? 
                            [1, 2, 3, 4, 5, 6, 7] :
                            selectedTimeSpan === 'month' ? 
                            (() => {
                                const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
                                const monthTicks = [];
                                for (let i = 1; i <= daysInMonth; i += 3) {
                                    monthTicks.push(i);
                                }
                                return monthTicks;
                            })() :
                            [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
                        }
                        tickFormat={healthData.length > 0 ? 
                            (() => {
                                // Format labels based on time span
                                switch(selectedTimeSpan) {
                                    case 'day':
                                        return ['12AM', '3AM', '6AM', '9AM', '12PM', '3PM', '6PM', '9PM'];
                                    case 'week':
                                        return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                                    case 'month':
                                        const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
                                        const monthLabels = [];
                                        for (let i = 1; i <= daysInMonth; i += 3) {
                                            monthLabels.push(i.toString());
                                        }
                                        return monthLabels;
                                    case 'year':
                                        return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                    default:
                                        return [];
                                }
                            })() :
                            selectedTimeSpan === 'day' ? 
                            ['12AM', '3AM', '6AM', '9AM', '12PM', '3PM', '6PM', '9PM'] :
                            selectedTimeSpan === 'week' ? 
                            ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] :
                            selectedTimeSpan === 'month' ? 
                            (() => {
                                const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
                                const monthLabels = [];
                                for (let i = 1; i <= daysInMonth; i += 3) {
                                    monthLabels.push(i.toString());
                                }
                                return monthLabels;
                            })() :
                            ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
                        }
                        style={{
                            axis: { stroke: '#8B949E' },
                            tickLabels: { fill: '#F0F6FC', fontSize: 21, padding: 5, fontWeight: 500 },
                            grid: { stroke: '#21262D', strokeWidth: 0.5 },
                        }}
                    />
                    
                    <VictoryAxis
                        dependentAxis
                        tickValues={dataType === 'heartrate' ? [40, 60, 80, 100, 120] :
                                     dataType === 'hrv' ? [0, 25, 50, 75, 100] :
                                     dataType === 'systolic' ? [90, 100, 110, 120, 130, 140] :
                                     dataType === 'diastolic' ? [50, 60, 70, 80, 90] :
                                     dataType === 'stress' ? [0, 25, 50, 75, 100] :
                                     dataType === 'dailysteps' ? [0, 3000, 6000, 9000, 12000, 15000] :
                                     dataType === 'dailycalories' ? [0, 100, 200, 300, 400, 500] :
                                     dataType === 'dailydistance' ? [0, 2, 4, 6, 8, 10] :
                                     dataType === 'spo2' ? [90, 92, 94, 96, 98, 100] :
                                     dataType === 'vo2max' ? [20, 30, 40, 50, 60] : [0, 25, 50, 75, 100]}
                        style={{
                            axis: { stroke: '#8B949E' },
                            tickLabels: { fill: '#F0F6FC', fontSize: 27, padding: 5, fontWeight: 500 },
                            grid: { stroke: '#8B949E', strokeWidth: 0.5, strokeDasharray: 'none' },
                        }}
                    />

                    {/* Error bars - Direct min/max vertical lines */}
                    {healthData.filter(d => d.hasData && d.min !== null && d.max !== null).map((datum, index) => (
                        <VictoryLine
                            key={`error-${index}`}
                            data={[
                                { x: datum.x, y: datum.min },
                                { x: datum.x, y: datum.max }
                            ]}
                            style={{
                                data: { 
                                    stroke: "#58A6FF",
                                    strokeWidth: 2
                                }
                            }}
                        />
                    ))}
                    {/* Horizontal caps at min */}
                    {healthData.filter(d => d.hasData && d.min !== null && d.max !== null).map((datum, index) => (
                        <VictoryLine
                            key={`cap-min-${index}`}
                            data={[
                                { x: datum.x - 0.1, y: datum.min },
                                { x: datum.x + 0.1, y: datum.min }
                            ]}
                            style={{
                                data: { 
                                    stroke: "#58A6FF",
                                    strokeWidth: 2
                                }
                            }}
                        />
                    ))}
                    {/* Horizontal caps at max */}
                    {healthData.filter(d => d.hasData && d.min !== null && d.max !== null).map((datum, index) => (
                        <VictoryLine
                            key={`cap-max-${index}`}
                            data={[
                                { x: datum.x - 0.1, y: datum.max },
                                { x: datum.x + 0.1, y: datum.max }
                            ]}
                            style={{
                                data: { 
                                    stroke: "#58A6FF",
                                    strokeWidth: 2
                                }
                            }}
                        />
                    ))}
                    
                    {/* Line Chart */}
                    <VictoryLine
                        data={healthData.filter(d => d.hasData)}
                        style={{
                            data: { stroke: "#58A6FF", strokeWidth: 2 }
                        }}
                    />
                    
                    {/* Data points for days with data */}
                    <VictoryScatter
                        data={healthData.filter(d => d.hasData)}
                        x="x"
                        y="y"
                        size={4}
                        style={{
                            data: { 
                                fill: "#FFFFFF",
                                stroke: "#58A6FF",
                                strokeWidth: 2
                            }
                        }}
                        labelComponent={<div />}
                    />
                    
                    {/* Indicators for missing data points */}
                    <VictoryScatter
                        data={healthData.filter(d => !d.hasData)}
                        x="x"
                        y={(d) => {
                            // Position at the middle of the Y domain for each data type
                            switch(dataType) {
                                case 'heartrate': return 80;
                                case 'hrv': return 50;
                                case 'systolic': return 115;
                                case 'diastolic': return 70;
                                case 'stress': return 50;
                                case 'dailysteps': return 7500;
                                case 'dailycalories': return 250;
                                case 'dailydistance': return 5;
                                case 'spo2': return 95;
                                case 'vo2max': return 40;
                                default: return 50;
                            }
                        }}
                        size={2}
                        style={{
                            data: { 
                                fill: "#d1d5db", // Light gray for missing data
                                stroke: "#9ca3af",
                                strokeWidth: 1
                            }
                        }}
                        symbol="diamond"
                        labelComponent={<div />}
                    />
                </VictoryChart>
                ) : (
                    <div className="flex items-center justify-center h-full text-white">
                        <div className="text-center">
                            <svg className="w-12 h-12 mx-auto mb-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                            <p className="text-white">No data available for {dataType}</p>
                        </div>
                    </div>
                )}
            </div>
            
            {/* Vital Information Card */}
            {getVitalInformationText() && (
                <div className="mt-4 p-3 bg-[#161B22] border border-[#30363D] rounded-md">
                    <div className="text-white text-sm">
                        <p>{getVitalInformationText()}</p>
                    </div>
                </div>
            )}
            
            {/* Trend Text Display */}
            {getTrendText() && (
                <div className="mt-4 p-3 bg-[#161B22] border border-[#30363D] rounded-md">
                    <div className="text-white text-sm">
                        <pre className="whitespace-pre-wrap font-mono text-xs text-white">
                            {getTrendText()}
                        </pre>
                    </div>
                </div>
            )}
            
            {/* Education Button */}
            <div className="flex justify-end mt-2 mb-20">
                <button
                    onClick={() => setShowEducation(true)}
                    className="px-4 py-2 bg-[#58A6FF] text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors duration-200"
                >
                    Education
                </button>
            </div>
        
        {/* Education Modal */}
        {showEducation && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-[#161B22] rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden border border-[#30363D]">
                    {/* Modal Header */}
                    <div className="flex items-center justify-between p-4 border-b border-[#30363D]">
                        <h3 className="text-lg font-semibold text-white">Education</h3>
                        <button
                            onClick={() => setShowEducation(false)}
                            className="text-white hover:text-gray-400 transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                    
                    {/* Modal Content */}
                    <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)]">
                        {educationLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                <span className="ml-3 text-white">Loading education content...</span>
                            </div>
                        ) : educationError ? (
                            <div className="text-center py-8">
                                <div className="text-red-400 mb-2">Error loading education content</div>
                                <div className="text-sm text-white">{educationError.message}</div>
                            </div>
                        ) : educationHtml ? (
                            <div
                                className="prose prose-lg max-w-none text-white"
                                style={{ color: '#F0F6FC' }}
                                dangerouslySetInnerHTML={{ __html: educationHtml }}
                            />
                        ) : (
                            <div className="text-center py-8 text-white">
                                No education content available
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )}

        {/* <BottomTab /> */}
            </div>
        </div>
    );
};

export default VisualizeWellnessData;